import { useMemo, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Building2,
  FileSpreadsheet,
  LineChart as LineChartIcon,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dfkFacturesAnalysis } from '../data/dfkFacturesAnalysis';

interface DfkFacturesAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'resume' | 'progression' | 'compagnies' | 'source';

const money = new Intl.NumberFormat('fr-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0,
});

const number = new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 1 });

const COLORS = ['#2563eb', '#7c3aed', '#dc2626', '#0891b2', '#16a34a', '#f59e0b', '#be123c', '#0f766e'];

function formatMoney(value: number) {
  return money.format(value);
}

function formatPercent(value?: number | null) {
  if (value === null || value === undefined) return 'n/a';
  return `${value > 0 ? '+' : ''}${number.format(value)} %`;
}

function compactMoney(value: number) {
  if (Math.abs(value) >= 1000) return `${number.format(value / 1000)} k$`;
  return `${number.format(value)} $`;
}

export default function DfkFacturesAnalysisModal({ isOpen, onClose }: DfkFacturesAnalysisModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('resume');

  const yearlyChart = useMemo(
    () => dfkFacturesAnalysis.yearlyTotals.map((row) => ({
      year: String(row.year),
      total: row.total,
      croissance: row.growthVsPrevious ?? 0,
      invoices: row.invoiceCount,
    })),
    []
  );

  const companyChart = useMemo(
    () => dfkFacturesAnalysis.companies.map((company) => ({
      company: company.company,
      '2024': company.totals['2024'],
      '2025': company.totals['2025'],
      '2026': company.totals['2026'],
      delta: company.delta2026Vs2025,
      growth: company.growth2026Vs2025,
      share: company.shareOf2026,
    })),
    []
  );

  const topDrivers = dfkFacturesAnalysis.companies
    .filter((company) => company.delta2026Vs2025 > 0)
    .slice()
    .sort((a, b) => b.delta2026Vs2025 - a.delta2026Vs2025)
    .slice(0, 5);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-[92vh] w-[94vw] max-w-7xl flex-col overflow-hidden rounded-3xl border border-sky-300/30 bg-slate-950 shadow-2xl">
          <div className="relative overflow-hidden border-b border-white/10 bg-gradient-to-r from-slate-950 via-blue-950 to-cyan-900 px-6 py-5 text-white">
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.55),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(124,58,237,0.45),transparent_32%)]" />
            <div className="relative flex items-start justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="rounded-2xl border border-cyan-300/50 bg-cyan-400/15 p-3 shadow-lg">
                  <FileSpreadsheet className="h-7 w-7 text-cyan-100" />
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-200">Comité CE · Analyse fournisseur stratégique</p>
                  <h2 className="mt-1 text-3xl font-black tracking-tight md:text-4xl">DFK — progression des factures BZ</h2>
                  <p className="mt-2 max-w-4xl text-sm leading-relaxed text-cyan-50/85">
                    Lecture exécutive des factures DFK des trois dernières années complètes du fichier source. Objectif : préparer une rencontre structurée avec Marc sur l’augmentation 2026, les causes, les compagnies touchées et les décisions à cadrer.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button onClick={onClose} className="rounded-full border border-white/20 bg-white/10 p-2 text-white transition hover:bg-white/20" type="button">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="border-b border-white/10 bg-slate-900/90 px-5 py-3">
            <div className="flex flex-wrap gap-2">
              {[
                ['resume', 'Résumé exécutif'],
                ['progression', 'Progression annuelle'],
                ['compagnies', 'Par compagnie'],
                ['source', 'Source & hypothèses'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id as TabId)}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                    activeTab === id
                      ? 'bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-500/20'
                      : 'border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-slate-950 px-6 py-6 text-slate-100">
            {activeTab === 'resume' && (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-4">
                  <KpiCard icon={<TrendingUp />} label="Total 2026" value={formatMoney(dfkFacturesAnalysis.kpis.total2026)} tone="cyan" />
                  <KpiCard icon={<AlertTriangle />} label="Hausse vs 2025" value={formatMoney(dfkFacturesAnalysis.kpis.delta2026Vs2025)} sub={formatPercent(dfkFacturesAnalysis.kpis.growth2026Vs2025)} tone="red" />
                  <KpiCard icon={<Building2 />} label="Part portée par BZ" value={`${formatPercent(dfkFacturesAnalysis.kpis.bz2026Share)}`} sub="du total 2026" tone="blue" />
                  <KpiCard icon={<Target />} label="Total 3 ans" value={formatMoney(dfkFacturesAnalysis.kpis.threeYearTotal)} tone="violet" />
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                  <Panel title="Message exécutif pour Marc" subtitle="Ce qu’il faut faire comprendre rapidement">
                    <div className="space-y-4 text-sm leading-relaxed text-slate-200">
                      <p>
                        Le groupe BZ passe de <strong>{formatMoney(dfkFacturesAnalysis.kpis.total2025)}</strong> en 2025 à <strong>{formatMoney(dfkFacturesAnalysis.kpis.total2026)}</strong> en 2026, soit une hausse de <strong>{formatMoney(dfkFacturesAnalysis.kpis.delta2026Vs2025)}</strong> (<strong>{formatPercent(dfkFacturesAnalysis.kpis.growth2026Vs2025)}</strong>).
                      </p>
                      <p>
                        L’augmentation n’est pas diffuse : elle est principalement concentrée dans <strong>BZ</strong>, qui représente <strong>{formatPercent(dfkFacturesAnalysis.kpis.bz2026Share)}</strong> du total 2026. Le signal à discuter n’est donc pas seulement “DFK coûte plus cher”, mais plutôt “quelle nature de travail DFK a transférée/facturée à BZ en 2026?”
                      </p>
                      <div className="rounded-2xl border border-red-400/30 bg-red-950/30 p-4">
                        <p className="font-bold text-red-100">Angle de rencontre recommandé</p>
                        <p className="mt-1 text-red-50/85">
                          Demander à Marc une ventilation claire de la facture majeure BZ 2026, la logique de récurrence/non-récurrence, et un plafond ou mécanisme d’approbation avant tout dépassement futur.
                        </p>
                      </div>
                    </div>
                  </Panel>

                  <Panel title="Top moteurs de hausse 2026" subtitle="Variation 2026 vs 2025">
                    <div className="space-y-3">
                      {topDrivers.map((company, index) => (
                        <div key={company.company} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-black text-white">{index + 1}. {company.company}</p>
                              <p className="text-xs text-slate-400">2026 : {formatMoney(company.totals['2026'])} · part {formatPercent(company.shareOf2026)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-base font-black text-emerald-300">+{formatMoney(company.delta2026Vs2025)}</p>
                              <p className="text-xs text-emerald-200/80">{formatPercent(company.growth2026Vs2025)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Panel>
                </div>
              </div>
            )}

            {activeTab === 'progression' && (
              <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <Panel title="Progression annuelle — groupe BZ" subtitle="Montants avant taxes, années 2024-2026">
                  <div className="h-[390px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={yearlyChart} margin={{ top: 25, right: 30, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.2)" />
                        <XAxis dataKey="year" stroke="#cbd5e1" />
                        <YAxis stroke="#cbd5e1" tickFormatter={compactMoney} />
                        <Tooltip formatter={(value: number) => formatMoney(value)} contentStyle={{ background: '#020617', border: '1px solid rgba(125,211,252,.35)', borderRadius: 14, color: '#fff' }} />
                        <Line type="monotone" dataKey="total" name="Total DFK" stroke="#22d3ee" strokeWidth={4} dot={{ r: 6, fill: '#22d3ee' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
                <Panel title="Lecture rapide" subtitle="Pourquoi 2026 ressort autant">
                  <div className="space-y-4 text-sm text-slate-200">
                    {dfkFacturesAnalysis.yearlyTotals.map((row) => (
                      <div key={row.year} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-lg font-black text-white">{row.year}</p>
                          <p className="text-lg font-black text-cyan-200">{formatMoney(row.total)}</p>
                        </div>
                        <p className="mt-1 text-xs text-slate-400">{row.invoiceCount} lignes · croissance {formatPercent(row.growthVsPrevious)}</p>
                      </div>
                    ))}
                    <div className="rounded-2xl border border-amber-400/30 bg-amber-950/25 p-4 text-amber-50/90">
                      2024 → 2025 est relativement stable (+3,7 %). 2026 change complètement la trajectoire avec +85,5 %, principalement par une charge importante concentrée sur BZ.
                    </div>
                  </div>
                </Panel>
              </div>
            )}

            {activeTab === 'compagnies' && (
              <div className="space-y-6">
                <Panel title="Comparatif par compagnie" subtitle="La contribution de chaque compagnie au total DFK">
                  <div className="h-[440px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={companyChart} margin={{ top: 25, right: 30, left: 10, bottom: 90 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
                        <XAxis dataKey="company" stroke="#cbd5e1" interval={0} angle={-35} textAnchor="end" height={95} />
                        <YAxis stroke="#cbd5e1" tickFormatter={compactMoney} />
                        <Tooltip formatter={(value: number) => formatMoney(value)} contentStyle={{ background: '#020617', border: '1px solid rgba(125,211,252,.35)', borderRadius: 14, color: '#fff' }} />
                        <Legend />
                        <Bar dataKey="2024" fill="#64748b" radius={[5, 5, 0, 0]} />
                        <Bar dataKey="2025" fill="#38bdf8" radius={[5, 5, 0, 0]} />
                        <Bar dataKey="2026" fill="#f97316" radius={[5, 5, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>

                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {dfkFacturesAnalysis.companies.map((company, index) => (
                    <div key={company.company} className="rounded-2xl border border-white/10 bg-slate-900/75 p-4 shadow-lg">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <p className="font-black text-white">{company.company}</p>
                        <span className="rounded-full px-2.5 py-1 text-xs font-bold text-slate-950" style={{ backgroundColor: COLORS[index % COLORS.length] }}>
                          {formatPercent(company.shareOf2026)} 2026
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <Metric label="2024" value={formatMoney(company.totals['2024'])} />
                        <Metric label="2025" value={formatMoney(company.totals['2025'])} />
                        <Metric label="2026" value={formatMoney(company.totals['2026'])} highlight />
                      </div>
                      <div className={`mt-3 rounded-xl p-3 text-sm font-bold ${company.delta2026Vs2025 >= 0 ? 'bg-emerald-950/40 text-emerald-200' : 'bg-slate-800 text-slate-300'}`}>
                        Variation 2026 : {company.delta2026Vs2025 >= 0 ? '+' : ''}{formatMoney(company.delta2026Vs2025)} · {formatPercent(company.growth2026Vs2025)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'source' && (
              <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
                <Panel title="Source officielle" subtitle="Document importé dans PRIsME Master">
                  <div className="space-y-4 text-sm text-slate-200">
                    <div className="rounded-2xl border border-cyan-300/25 bg-cyan-950/20 p-5">
                      <p className="text-xs uppercase tracking-wide text-cyan-300">Fichier</p>
                      <p className="mt-1 text-xl font-black text-white">{dfkFacturesAnalysis.source.fileName}</p>
                      <p className="mt-2 text-slate-300">{dfkFacturesAnalysis.source.driveFolder}</p>
                    </div>
                    <div className="rounded-2xl border border-amber-300/25 bg-amber-950/20 p-4 text-sm leading-relaxed text-amber-100">
                      Le fichier source n’est pas exposé dans le site public. Il demeure accessible depuis l’emplacement Drive interne indiqué ci-dessus.
                    </div>
                  </div>
                </Panel>

                <Panel title="Hypothèses d’analyse" subtitle="Ce qui est inclus / exclu">
                  <ul className="space-y-3 text-sm leading-relaxed text-slate-200">
                    <li>• Montants utilisés : <strong>Total avant taxes</strong>.</li>
                    <li>• Période principale : <strong>2024, 2025, 2026</strong>, soit les trois années complètes à présenter.</li>
                    <li>• Une ligne <strong>2027</strong> existe dans le fichier, mais elle est exclue de la tendance 3 ans pour éviter de mélanger une année future/partielle avec l’analyse exécutive.</li>
                    <li>• Les lignes “estimé” sont conservées parce qu’elles sont dans le fichier source et participent à la comparaison fournie.</li>
                    <li>• Le signal central : la hausse 2026 est concentrée sur BZ, pas répartie uniformément sur toutes les compagnies.</li>
                  </ul>
                </Panel>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function KpiCard({ icon, label, value, sub, tone }: { icon: ReactNode; label: string; value: string; sub?: string; tone: 'cyan' | 'red' | 'blue' | 'violet' }) {
  const toneClass = {
    cyan: 'border-cyan-300/35 bg-cyan-950/35 text-cyan-100',
    red: 'border-red-300/35 bg-red-950/35 text-red-100',
    blue: 'border-blue-300/35 bg-blue-950/35 text-blue-100',
    violet: 'border-violet-300/35 bg-violet-950/35 text-violet-100',
  }[tone];
  return (
    <div className={`rounded-3xl border p-5 shadow-xl ${toneClass}`}>
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 [&>svg]:h-6 [&>svg]:w-6">{icon}</div>
      <p className="text-xs font-bold uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-2xl font-black text-white">{value}</p>
      {sub && <p className="mt-1 text-sm font-bold opacity-85">{sub}</p>}
    </div>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="rounded-3xl border border-white/10 bg-slate-900/65 p-5 shadow-xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-black text-white">{title}</h3>
          {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
        </div>
        <LineChartIcon className="h-5 w-5 text-cyan-300" />
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-2 ${highlight ? 'border-orange-300/40 bg-orange-950/30' : 'border-white/10 bg-white/[0.03]'}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 font-black text-white">{value}</p>
    </div>
  );
}
